/**
 * useFollowerStats - React Query hooks for follower statistics
 *
 * Provides data fetching and mutations for client follower stats.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";
import type {
  FollowerStatsMap,
  ClientFollowerStatsData,
  FollowerStats,
} from "@/types";

const followerStatsKeys = {
  all: ["follower-stats"] as const,
  client: (clientId: string) => ["follower-stats", clientId] as const,
};

/**
 * Fetches follower stats for all clients (batch load for dashboard)
 */
export function useAllFollowerStats() {
  return useQuery({
    queryKey: followerStatsKeys.all,
    queryFn: async (): Promise<FollowerStatsMap> => {
      const response = await apiClient.get<FollowerStatsMap>(
        "/api/social-media/follower-stats"
      );
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 min cache, data refreshes daily anyway
  });
}

/**
 * Fetches follower stats for a specific client
 */
export function useClientFollowerStats(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? followerStatsKeys.client(clientId) : ["follower-stats", "none"],
    queryFn: async (): Promise<ClientFollowerStatsData | null> => {
      if (!clientId) return null;
      const response = await apiClient.get<ClientFollowerStatsData>(
        `/api/social-media/follower-stats/${clientId}`
      );
      return response;
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}

interface RefreshResult {
  success: boolean;
  stats?: FollowerStats;
  error?: string;
}

/**
 * Mutation for refreshing follower stats for a single client
 */
export function useRefreshFollowerStatsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientId: string): Promise<RefreshResult> => {
      const response = await apiClient.post<RefreshResult>(
        `/api/social-media/refresh-followers/${clientId}`
      );
      return response;
    },
    onSuccess: (_result, clientId) => {
      queryClient.invalidateQueries({ queryKey: followerStatsKeys.all });
      queryClient.invalidateQueries({ queryKey: followerStatsKeys.client(clientId) });
    },
  });
}

interface SyncSummary {
  synced: number;
  skipped: number;
  failed: number;
  errors: Array<{ client_id: string; error: string }>;
}

interface AdminSyncResult {
  status: string;
  sync_id: string;
}

/**
 * Mutation for admin to sync all follower stats.
 * Returns 202 immediately; results arrive via SSE events.
 */
export function useAdminSyncFollowerStatsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rateLimitDelay?: number): Promise<AdminSyncResult> => {
      const response = await apiClient.post<AdminSyncResult>(
        "/api/social-media/admin/sync-follower-stats",
        rateLimitDelay ? { rate_limit_delay: rateLimitDelay } : undefined
      );
      return response;
    },
    onSuccess: () => {
      // Cache invalidation will happen when SSE follower_sync_complete fires
      queryClient.invalidateQueries({ queryKey: followerStatsKeys.all });
    },
  });
}

/**
 * Utility for cache invalidation
 */
export const followerStatsCacheUtils = {
  invalidateAll(queryClient: ReturnType<typeof useQueryClient>): void {
    queryClient.invalidateQueries({ queryKey: followerStatsKeys.all });
  },
  invalidateClient(
    clientId: string,
    queryClient: ReturnType<typeof useQueryClient>
  ): void {
    queryClient.invalidateQueries({ queryKey: followerStatsKeys.client(clientId) });
  },
};
